import React from 'react';
import { Route, Navigate, useLocation } from 'react-router';
import { Spin, Result, Button } from 'antd';
import { useGetIdentity } from '@refinedev/core';
import { User } from '@src/types';
import { useNavigate } from 'react-router';
import { AuthenticatedLayout } from './guards';
import { ROUTES } from './constants';
import { AppointmentsList } from '@src/pages/appointments/page';
import { LeadsList } from '@src/pages/leads/page';
import { PatientsList } from '@src/pages/patients/page';
import { PatientDetailsPage } from '@src/pages/patients/details/PatientDetailsPage';
import { OfferingsList } from '@src/pages/offerings/page';
import { BranchesList } from '@src/pages/branches/page';
import { DashboardPage } from '@src/pages/dashboard/page';
import { UsersPage } from '@src/pages/users/page';
import { OrganizationsPage } from '@src/pages/organizations/page';
import { MedicalPage } from '@src/pages/medical';
import { InsideStockPage } from '@src/pages/inside-stock/index';
import { FollowUpsPage } from '@src/pages/followups/page';
import { PaymentsPage } from '@src/pages/payments/page';
import { MainLayout } from '@src/components/Layout';
import { RESTRICTED_ALLOWED_EMAILS } from './restrictedEmails';
import { canAccessRoute, getDefaultRouteForUser, isMarketingUser } from '@src/utils/userAccess';

// Access Control Wrapper Component
const MasterAdminOnly: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { data: user, isLoading } = useGetIdentity<User>();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <Spin size="large" />
        <div>Checking permissions...</div>
      </div>
    );
  }

  if (user?.type !== 'MASTER_ADMIN') {
    return (
      <Result
        status="403"
        title="Access Denied"
        subTitle="Sorry, you don't have permission to access this page."
        extra={[
          <Button
            type="primary"
            key="appointments"
            onClick={() => navigate(ROUTES.APPOINTMENTS)}
          >
            Go to Appointments
          </Button>,
        ]}
      />
    );
  }

  return <>{children}</>;
};

// Access Control Wrapper for Admin+ users (MASTER_ADMIN, ADMIN, DOCTOR)
const AdminPlusOnly: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { data: user, isLoading } = useGetIdentity<User>();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <Spin size="large" />
        <div>Checking permissions...</div>
      </div>
    );
  }

  if (!['MASTER_ADMIN', 'ADMIN', 'DOCTOR'].includes(user?.type || '')) {
    return (
      <Result
        status="403"
        title="Access Denied"
        subTitle="Sorry, you don't have permission to access this page. Only Master Admin, Admin, and Doctor users can access this feature."
        extra={[
          <Button
            type="primary"
            key="dashboard"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </Button>,
        ]}
      />
    );
  }

  return <>{children}</>;
};

// Access Control Wrapper for non-MASTER_ADMIN users
const NonMasterAdminOnly: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { data: user, isLoading } = useGetIdentity<User>();
  const navigate = useNavigate();
  const location = useLocation();

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <Spin size="large" />
        <div>Checking permissions...</div>
      </div>
    );
  }

  if (user?.type === 'MASTER_ADMIN') {
    return (
      <Result
        status="403"
        title="Access Denied"
        subTitle="Sorry, Master Admin users cannot access this page. You can only access Organizations and Users management."
        extra={[
          <Button
            type="primary"
            key="dashboard"
            onClick={() => navigate(getDefaultRouteForUser(user?.type))}
          >
            Go to Dashboard
          </Button>,
        ]}
      />
    );
  }

  if (!canAccessRoute(user?.type, location.pathname)) {
    return (
      <Result
        status="403"
        title="Access Denied"
        subTitle="Sorry, you don't have permission to access this page."
        extra={[
          <Button
            type="primary"
            key="home"
            onClick={() => navigate(getDefaultRouteForUser(user?.type))}
          >
            Go to Home
          </Button>,
        ]}
      />
    );
  }

  return <>{children}</>;
};

const RestrictedByAllowedEmails: React.FC<{
  children: React.ReactNode;
  pageLabel: string;
}> = ({
  children,
  pageLabel,
}) => {
  const { data: user, isLoading } = useGetIdentity<User>();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <Spin size="large" />
        <div>Checking permissions...</div>
      </div>
    );
  }

  const email = (user?.email || '').trim().toLowerCase();
  const allowed =
    isMarketingUser(user?.type) ||
    RESTRICTED_ALLOWED_EMAILS.some((e) => e.toLowerCase() === email);
  if (!allowed) {
    return (
      <Result
        status="403"
        title="Access Denied"
        subTitle={`You don't have permission to access the ${pageLabel} page.`}
        extra={[
          <Button
            type="primary"
            key="appointments"
            onClick={() => navigate(ROUTES.APPOINTMENTS)}
          >
            Go to Appointments
          </Button>,
        ]}
      />
    );
  }

  return <>{children}</>;
};

const DefaultRedirect: React.FC = () => {
  const { data: user, isLoading } = useGetIdentity<User>();

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return <Navigate to={getDefaultRouteForUser(user?.type)} replace />;
};

export function getProtectedRoutes() {
  return [
    <Route element={<AuthenticatedLayout />} key="protected">
      <Route element={<MainLayout />}>
        <Route index element={<DefaultRedirect />} />
        <Route
          path={ROUTES.REPORTS}
          element={
            <RestrictedByAllowedEmails pageLabel="Reports">
              <DashboardPage />
            </RestrictedByAllowedEmails>
          }
        />
        <Route path={ROUTES.APPOINTMENTS}>
          <Route
            index
            element={
              <NonMasterAdminOnly>
                <AppointmentsList />
              </NonMasterAdminOnly>
            }
          />
        </Route>
        <Route path={ROUTES.LEADS}>
          <Route
            index
            element={
              <NonMasterAdminOnly>
                <LeadsList />
              </NonMasterAdminOnly>
            }
          />
        </Route>
        <Route path={ROUTES.PATIENTS}>
          <Route
            index
            element={
              <NonMasterAdminOnly>
                <PatientsList />
              </NonMasterAdminOnly>
            }
          />
          <Route
            path=":id"
            element={
              <NonMasterAdminOnly>
                <PatientDetailsPage />
              </NonMasterAdminOnly>
            }
          />
        </Route>
        <Route path={ROUTES.OFFERINGS}>
          <Route
            index
            element={
              <NonMasterAdminOnly>
                <OfferingsList />
              </NonMasterAdminOnly>
            }
          />
        </Route>
        <Route path={ROUTES.BRANCHES}>
          <Route
            index
            element={
              <MasterAdminOnly>
                <BranchesList />
              </MasterAdminOnly>
            }
          />
        </Route>
        <Route path={ROUTES.USERS}>
          <Route
            index
            element={
              <AdminPlusOnly>
                <UsersPage />
              </AdminPlusOnly>
            }
          />
        </Route>
        <Route path={ROUTES.ORGANIZATIONS}>
          <Route
            index
            element={
              <MasterAdminOnly>
                <OrganizationsPage />
              </MasterAdminOnly>
            }
          />
        </Route>
        <Route path={ROUTES.MEDICAL}>
          <Route
            index
            element={
              <NonMasterAdminOnly>
                <MedicalPage />
              </NonMasterAdminOnly>
            }
          />
        </Route>
        <Route path={ROUTES.INSIDE_STOCK}>
          <Route
            index
            element={
              <NonMasterAdminOnly>
                <InsideStockPage />
              </NonMasterAdminOnly>
            }
          />
        </Route>
        <Route path={ROUTES.TASKS}>
          <Route
            index
            element={
              <NonMasterAdminOnly>
                <FollowUpsPage />
              </NonMasterAdminOnly>
            }
          />
        </Route>
        <Route path={ROUTES.PAYMENTS}>
          <Route
            index
            element={
              <RestrictedByAllowedEmails pageLabel="Payments">
                <NonMasterAdminOnly>
                  <PaymentsPage />
                </NonMasterAdminOnly>
              </RestrictedByAllowedEmails>
            }
          />
        </Route>
      </Route>
    </Route>,
  ];
}
